import { useState, useEffect } from "react";
import { useRouter } from "next/navigation";
import Image from "next/image";
import { ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { API_BASE } from "@/lib/api";
import {
  EventPrizePreview,
  hasEventPrizePreview,
} from "@/components/events/PrizeDisplay";
import type { MainEventSummary } from "@/services/events/main-event";
import { asset } from "@/lib/asset";

const SPORT_ICONS: Record<string, string> = {
  NBA: asset("assets/sports/basketball.webp"),
  NCAA_BASKETBALL: asset("assets/sports/basketball.webp"),
  NHL: asset("assets/sports/hockey.webp"),
  NFL: asset("assets/sports/football.webp"),
  MLB: asset("assets/sports/baseball.webp"),
  SOCCER: asset("assets/sports/soccer.webp"),
  SOCCER_EPL: asset("assets/sports/soccer.webp"),
  SOCCER_WORLD_CUP: asset("assets/sports/soccer.webp"),
  SOCCER_UCL: asset("assets/sports/soccer.webp"),
  MMA: asset("assets/sports/mma.webp"),
  F1: asset("assets/sports/f1.webp"),
  GOLF: asset("assets/sports/golf.webp"),
  TENNIS: asset("assets/sports/tennis.webp"),
  HORSE_RACING: asset("assets/sports/horse-racing.webp"),
  ESPORTS: asset("assets/sports/esports.webp"),
};

const STATUS_COLORS: Record<string, { bg: string; text: string }> = {
  UPCOMING: { bg: "bg-blue-500/10 border-blue-500/30", text: "text-blue-400" },
  OPEN: { bg: "bg-verdigris/10 border-verdigris/30", text: "text-verdigris" },
  LOCKED: { bg: "bg-gold/10 border-gold/30", text: "text-gold" },
  COMPLETED: { bg: "bg-tertiary/10 border-tertiary/30", text: "text-tertiary" },
};

function MainEventCard({
  event,
  onClick,
}: {
  event: MainEventSummary;
  onClick: () => void;
}) {
  const sportIcon =
    SPORT_ICONS[event.sport] || asset("assets/sports/basketball.webp");
  const statusStyle = STATUS_COLORS[event.status] || STATUS_COLORS.UPCOMING;
  const startDate = new Date(event.startTime).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
  const endDate = new Date(event.endTime).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
  const hasPrize = hasEventPrizePreview(event);

  return (
    <button
      onClick={onClick}
      className="group relative w-full overflow-hidden rounded-2xl border border-parchment-border bg-parchment text-left shadow-sm transition-[border-color,transform,box-shadow] duration-300 hover:-translate-y-0.5 hover:border-gold/40 hover:shadow-md"
    >
      {hasPrize && (
        <span className="absolute right-10 top-0 z-10 rounded-b-md bg-gold px-3 pb-1.5 pt-1 font-mono text-[8px] font-bold uppercase tracking-[0.2em] text-surface shadow-sm transition-transform duration-300 group-hover:translate-y-0.5 sm:right-12 sm:text-[9px]">
          top rewards
          <span
            aria-hidden="true"
            className="absolute left-1/2 top-full h-0 w-0 -translate-x-1/2 border-x-[5px] border-t-[4px] border-x-transparent border-t-gold"
          />
        </span>
      )}
      <div className="grid grid-cols-[auto_minmax(0,1fr)_auto] items-start gap-3 p-4 sm:gap-4 sm:p-5 md:grid-cols-[auto_minmax(0,1fr)_minmax(250px,auto)_auto]">
        <Image
          src={sportIcon}
          alt={event.sport}
          width={56}
          height={56}
          className="w-11 h-11 sm:w-14 sm:h-14 object-contain opacity-85 flex-shrink-0 mt-0.5"
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span
              className={cn(
                "px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider border",
                statusStyle.bg,
                statusStyle.text,
              )}
            >
              {event.status}
            </span>
            {event.entryFee === 0 && (
              <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-verdigris/10 border border-verdigris/30 text-verdigris">
                Free
              </span>
            )}
          </div>
          <h3 className="text-surface font-serif font-bold text-base sm:text-lg leading-tight mb-1">
            {event.title}
          </h3>
          {event.description && (
            <p className="text-tertiary text-xs sm:text-sm mb-2 line-clamp-2">
              {event.description}
            </p>
          )}
          <div className="flex items-center gap-x-3 gap-y-1 sm:gap-4 text-[11px] sm:text-xs text-warm-gray flex-wrap">
            <span>
              {startDate} — {endDate}
            </span>
            <span className="flex items-center gap-1">
              <Image
                src={asset("assets/icons/bench.webp")}
                alt=""
                width={14}
                height={14}
              />
              {event.entryCount} entered
            </span>
            <span>{event.undercardCount} rounds</span>
          </div>
        </div>

        <EventPrizePreview
          event={event}
          coinIconSrc={asset("assets/icons/coins.webp")}
        />

        <ChevronRight className="col-start-3 row-start-1 h-5 w-5 self-center text-tertiary transition-colors group-hover:text-gold md:col-start-4" />
      </div>
    </button>
  );
}

export default function EventsPage() {
  const router = useRouter();
  const [events, setEvents] = useState<MainEventSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetch(`${API_BASE}/api/main-events`)
      .then((res) => (res.ok ? res.json() : Promise.reject("Failed to fetch")))
      .then((data) => setEvents(data))
      .catch((err) =>
        setError(typeof err === "string" ? err : "Failed to load events"),
      )
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-gold border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-warm-gray">Loading events...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface">
      <div className="ornamental-rule max-w-7xl mx-auto px-4 sm:px-6 pt-6 sm:pt-8" />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <div className="mb-6 sm:mb-8">
          <span className="text-gold text-[10px] sm:text-xs font-bold tracking-[0.2em] uppercase mb-3 sm:mb-4 block">
            Events
          </span>
          <h1 className="text-2xl sm:text-3xl font-serif font-bold text-cream mb-2">
            Compete &amp; Win
          </h1>
          <p className="text-sm sm:text-base text-warm-gray">
            Enter main events and pick winners across every round.
          </p>
        </div>

        {error && (
          <div className="p-4 mb-6 bg-oxblood/10 border border-oxblood/30 rounded-xl text-oxblood text-sm">
            {error}
          </div>
        )}

        {events.length === 0 && !error && (
          <p className="text-tertiary py-12 text-center">
            No events available right now.
          </p>
        )}

        <div className="space-y-4">
          {events.map((event) => (
            <MainEventCard
              key={event.id}
              event={event}
              onClick={() => router.push(`/events/main/${event.id}`)}
            />
          ))}
        </div>
      </main>
    </div>
  );
}
